/**
 * Customer sign in / sign up — phone number first, then a passcode.
 *
 * The number is checked to decide the next step: existing customers with a
 * passcode enter it; new numbers choose one; anyone can fall back to an SMS
 * code (which is also how a forgotten passcode is recovered).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, MessageSquare, Phone, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { platformApi } from "@/platform/api";
import { usePlatformAuth } from "@/platform/PlatformAuthContext";

type Step = "phone" | "passcode" | "create" | "otp";

export default function PhoneAuth() {
  const { requestOtp, verifyOtp, refreshUser } = usePlatformAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("phone");
  const [phone, setPhone] = useState("");
  const [passcode, setPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [fullName, setFullName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const focusRef = useRef<HTMLInputElement>(null);

  useEffect(() => { focusRef.current?.focus(); }, [step]);

  const fail = (error: unknown, title = "Something went wrong") =>
    toast({
      title,
      description: error instanceof Error ? error.message : "Please try again.",
      variant: "destructive",
    });

  const done = (isNew: boolean) => {
    toast({
      title: isNew ? "Welcome to Ngwees Capital" : "Welcome back",
      description: isNew ? "Your account is ready." : undefined,
    });
    navigate("/dashboard");
  };

  // Step 1 — which path does this number take?
  const continueWithPhone = async () => {
    setBusy(true);
    try {
      const status = await platformApi.checkPhone(phone);
      setStep(status.registered && status.has_passcode ? "passcode"
        : status.registered ? "otp" : "create");
      if (status.registered && !status.has_passcode) {
        await requestOtp(phone);
        toast({ title: "Code sent", description: "Check your SMS messages." });
      }
    } catch (error) {
      fail(error, "Check your number");
    } finally {
      setBusy(false);
    }
  };

  const signIn = async () => {
    setBusy(true);
    try {
      await platformApi.passcodeLogin(phone, passcode);
      await refreshUser();
      done(false);
    } catch (error) {
      fail(error, "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const createAccount = async () => {
    if (passcode !== confirmPasscode) {
      toast({ title: "Passcodes do not match", variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      await platformApi.passcodeRegister(phone, passcode, fullName || undefined);
      await refreshUser();
      done(true);
    } catch (error) {
      fail(error, "Could not create your account");
    } finally {
      setBusy(false);
    }
  };

  const useSmsInstead = async () => {
    setBusy(true);
    try {
      await requestOtp(phone);
      setStep("otp");
      toast({ title: "Code sent", description: "Check your SMS messages." });
    } catch (error) {
      fail(error, "Could not send the code");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setBusy(true);
    try {
      const { isNewUser } = await verifyOtp(phone, code);
      done(isNewUser);
    } catch (error) {
      fail(error, "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const digitsOnly = (value: string) => value.replace(/\D/g, "");

  const titles: Record<Step, { title: string; description: string }> = {
    phone: {
      title: "Sign in",
      description: "New here? The same step creates your account — no forms.",
    },
    passcode: { title: "Enter your passcode", description: `Signing in as ${phone}` },
    create: { title: "Create your passcode", description: "You'll use it to sign in from now on." },
    otp: { title: "Enter your code", description: `We sent a 6-digit code to ${phone}.` },
  };

  return (
    <div className="premium-page flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{titles[step].title}</CardTitle>
          <CardDescription>{titles[step].description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === "phone" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="phone">Mobile number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="phone" ref={focusRef} className="pl-9" inputMode="tel"
                    autoComplete="tel" placeholder="097X XXX XXX" value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && phone && continueWithPhone()} />
                </div>
                <p className="text-xs text-muted-foreground">
                  We only use your number to identify your account.
                </p>
              </div>
              <Button className="w-full" disabled={busy || digitsOnly(phone).length < 9}
                onClick={continueWithPhone}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
              </Button>
            </>
          )}

          {step === "passcode" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="passcode">Passcode</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="passcode" ref={focusRef} type="password" className="pl-9 tracking-[0.4em]"
                    inputMode="numeric" autoComplete="current-password" maxLength={12}
                    value={passcode} onChange={(e) => setPasscode(digitsOnly(e.target.value))}
                    onKeyDown={(e) => e.key === "Enter" && passcode.length >= 5 && signIn()} />
                </div>
              </div>
              <Button className="w-full" disabled={busy || passcode.length < 5} onClick={signIn}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
              <Button variant="ghost" className="w-full" disabled={busy} onClick={useSmsInstead}>
                <MessageSquare className="mr-2 h-4 w-4" />
                Forgot passcode — send an SMS code
              </Button>
              <Button variant="link" className="w-full" onClick={() => { setStep("phone"); setPasscode(""); }}>
                Use a different number
              </Button>
            </>
          )}

          {step === "create" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="name">Your name (optional)</Label>
                <Input id="name" ref={focusRef} value={fullName}
                  onChange={(e) => setFullName(e.target.value)} placeholder="Chanda Mwansa" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-passcode">Choose a passcode</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="new-passcode" type="password" className="pl-9 tracking-[0.4em]"
                    inputMode="numeric" autoComplete="new-password" maxLength={12}
                    value={passcode} onChange={(e) => setPasscode(digitsOnly(e.target.value))} />
                </div>
                <p className="text-xs text-muted-foreground">
                  At least 5 digits. Avoid 12345 or repeated digits.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-passcode">Confirm passcode</Label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="confirm-passcode" type="password" className="pl-9 tracking-[0.4em]"
                    inputMode="numeric" maxLength={12} value={confirmPasscode}
                    onChange={(e) => setConfirmPasscode(digitsOnly(e.target.value))}
                    onKeyDown={(e) => e.key === "Enter" && passcode.length >= 5 && createAccount()} />
                </div>
              </div>
              <Button className="w-full" disabled={busy || passcode.length < 5 || !confirmPasscode}
                onClick={createAccount}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
              </Button>
              <Button variant="link" className="w-full" onClick={() => setStep("phone")}>
                Use a different number
              </Button>
            </>
          )}

          {step === "otp" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="code">6-digit code</Label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input id="code" ref={focusRef} className="pl-9 tracking-[0.4em]"
                    inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                    value={code} onChange={(e) => setCode(digitsOnly(e.target.value))}
                    onKeyDown={(e) => e.key === "Enter" && code.length === 6 && verifyCode()} />
                </div>
              </div>
              <Button className="w-full" disabled={busy || code.length !== 6} onClick={verifyCode}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & continue"}
              </Button>
              <Button variant="ghost" className="w-full" disabled={busy} onClick={useSmsInstead}>
                Resend code
              </Button>
              <Button variant="link" className="w-full" onClick={() => { setStep("phone"); setCode(""); }}>
                Use a different number
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
